import {
  deepCloneJson,
  getAtPath,
  isNonEmptyValue,
  isRecord,
} from './internal';
import { walkTranslatableUnits } from './walk';
import {
  H5PParams,
  H5PSemantics,
  MergeOptions,
  TranslatableField,
  TranslationMap,
  TranslationValue,
  TRANSLATABLE_FIELD_TYPES,
} from './types';

/**
 * Deep copy of `params` with translated values swapped in. Inputs are never
 * mutated, per-field fallback keeps the source, malformed params never throw.
 */
export const mergeTranslations = (
  semantics: H5PSemantics,
  params: H5PParams,
  map: TranslationMap | undefined | null,
  options?: MergeOptions,
): H5PParams => {
  const merged = deepCloneJson(params);
  if (!map || Object.keys(map).length === 0) {
    return merged;
  }
  // Walk the clone so path segments are guaranteed to match the object we
  // write into.
  const { fields } = walkTranslatableUnits(semantics, merged, options);
  for (const field of fields) {
    if (field.mapKey === null) {
      continue;
    }
    const value = map[field.mapKey];
    if (!isNonEmptyValue(value) || !valueMatchesType(field, value)) {
      continue;
    }
    applyValue(merged, field, deepCloneJson(value));
  }
  return merged;
};

const valueMatchesType = (
  field: TranslatableField,
  value: TranslationValue,
): boolean => {
  if (
    field.type === TRANSLATABLE_FIELD_TYPES.TEXT ||
    field.type === TRANSLATABLE_FIELD_TYPES.HTML
  ) {
    return typeof value === 'string';
  }
  return typeof value !== 'string';
};

const applyValue = (
  merged: H5PParams,
  field: TranslatableField,
  value: TranslationValue,
): void => {
  const segments = field.pathSegments;
  if (segments.length === 0) {
    return;
  }
  if (field.libraryAsset) {
    // Swap only the embed's `params`; require an actual embed in the source
    // (never fabricate a library object around a translated asset).
    const libraryValue = getAtPath(merged, segments.slice(0, -1));
    if (
      !isRecord(libraryValue) ||
      typeof libraryValue['library'] !== 'string'
    ) {
      return;
    }
    libraryValue['params'] = value;
    return;
  }
  setAtPath(merged, segments, value);
};

/**
 * Write at a params path, creating missing PLAIN-OBJECT containers (an optional
 * group the source never filled). Missing array indices mean divergence: skip.
 */
const setAtPath = (
  root: H5PParams,
  segments: ReadonlyArray<string | number>,
  value: TranslationValue,
): void => {
  let current: unknown = root;
  for (let i = 0; i < segments.length - 1; i++) {
    const segment = segments[i];
    if (typeof segment === 'number') {
      if (!Array.isArray(current) || segment >= current.length) {
        return;
      }
      current = current[segment];
    } else {
      if (!isRecord(current)) {
        return;
      }
      if (current[segment] === undefined || current[segment] === null) {
        // Only create a container when the next step is an object key.
        if (typeof segments[i + 1] === 'number') {
          return;
        }
        current[segment] = {};
      }
      current = current[segment];
    }
  }
  const last = segments[segments.length - 1];
  if (typeof last === 'number') {
    if (Array.isArray(current) && last < current.length) {
      current[last] = value;
    }
    return;
  }
  if (isRecord(current)) {
    current[last] = value;
  }
};
